import type { PaletteMode } from '@mui/material';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import type { RootState } from '../../RootState';

import type { AppsView, AssetsView, ITimelineState } from './state';
import { uiInitialState } from './state';
import { fetchUserSettings } from './actions';

const uiSlice = createSlice({
  name: 'ui',
  initialState: uiInitialState,
  reducers: {
    setTimelineState: (state, action: PayloadAction<Partial<ITimelineState>>) => {
      state.timeline = { ...state.timeline, ...action.payload };
    },
    setAssetsView: (state, action: PayloadAction<AssetsView>) => {
      state.assetsView = action.payload;
    },
    setAppsView: (state, action: PayloadAction<AppsView>) => {
      state.appsView = action.payload;
    },
    toggleNavSidebar: (state, action: PayloadAction<boolean | undefined>) => {
      if (typeof action.payload === 'boolean') {
        state.navSidebarOpen = action.payload;
      } else {
        state.navSidebarOpen = !state.navSidebarOpen;
      }
    },
    toggleTimeline: (state) => {
      state.timeline.open = !state.timeline.open;
      if (state.timeline.open) {
        state.timeline.filterBy = {};
      }
    },
    setCurrentThemeMode: (state, action: PayloadAction<PaletteMode>) => {
      state.currentThemeMode = action.payload;
    },
    setTableTimeRangeMode: (state, action: PayloadAction<boolean>) => {
      state.isTableTimeRangeRelative = action.payload;
    },
    setOpenControlBarPromotionDialog: (state, action: PayloadAction<boolean>) => {
      state.openControlBarPromotionDialog = action.payload;
    },
    update: (state, action: PayloadAction<Record<string, unknown>>) => {
      _.merge(state, action.payload);
    },
    rehydrate: (state) => {
      _.merge(state, uiInitialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserSettings.fulfilled, (state, { payload }) => {
      if (payload) {
        state.isHints = payload.isHints;
        state.isSplashScreen = payload.isSplashScreen;
      }
    });
    builder.addCase(fetchUserSettings.rejected, (state) => {
      state.isHints = true;
      state.isSplashScreen = false;
    });
  },
});

export const uiToolkit = Object.freeze({
  initialState: uiInitialState,
  actions: uiSlice.actions,
  selectors: {
    state: (state: RootState) => state.ui,
    rehydrate: (state: RootState) => state.ui,
    timeline: (state: RootState) => state.ui.timeline,
    assetsView: (state: RootState) => state.ui.assetsView,
    appsView: (state: RootState) => state.ui.appsView,
    navSidebarOpen: (state: RootState) => state.ui.navSidebarOpen,
    navSidebarSide: (state: RootState) => state.ui.navSidebarSide,
    currentThemeMode: (state: RootState) => state.ui.currentThemeMode,
    isHints: (state: RootState) => state.ui.isHints,
    isSplashScreen: (state: RootState) => state.ui.isSplashScreen,
    isTableTimeRangeRelative: (state: RootState) => state.ui.isTableTimeRangeRelative,
    openControlBarPromotionDialog: (state: RootState) => state.ui.openControlBarPromotionDialog,
    byKey: (key: string) => (state: RootState) => state.ui[key],
  },
});

export const uiReducer = uiSlice.reducer;
